"use client";

import Link from "next/link";
import React, {
  Children,
  cloneElement,
  isValidElement,
  memo,
  useCallback,
  useMemo,
  type ReactElement,
  type ReactNode,
} from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useArtifact } from "@/hooks/use-artifact";
import { CodeBlock } from "./code-block";

const CELL_CITATION_REGEX = /\[C:(\d+)\]/g;

// Matches a fenced code block (``` ... ```, optional language tag) whose entire
// body is only citations, whitespace, and/or trailing punctuation. We strip
// the fence and keep the citations inline so they don't render as the gray
// CodeBlock box around `[C:n]` badges.
const CITATION_ONLY_FENCE_REGEX =
  /```[^\n`]*\n?((?:\s*\[C:\d+\]\s*)+[\s.,;:!?]*)\n?```/g;

// Matches inline single-backtick code that wraps a citation, e.g. `[C:1]`.
const CITATION_INLINE_CODE_REGEX = /`\s*(\[C:\d+\](?:\s*\[C:\d+\])*)\s*`/g;

// Matches a citation (or run of citations) that landed on its own line after
// a newline — we pull it back up with a single space.
const CITATION_ON_OWN_LINE_REGEX =
  /[ \t]*\n[ \t]*(\[C:\d+\](?:[ \t]*\[C:\d+\])*)/g;

// Matches stranded sentence-ending punctuation that got pushed to its own
// line right after a citation group.
const CITATION_ORPHAN_PUNCT_REGEX =
  /(\[C:\d+\](?:[ \t]*\[C:\d+\])*)[ \t]*\n[ \t]*([.,;:!?])/g;

function normalizeCitations(text: string): string {
  if (!text || text.indexOf("[C:") === -1) {
    return text;
  }

  return text
    .replace(CITATION_ONLY_FENCE_REGEX, (_m, inner) => ` ${inner.trim()} `)
    .replace(CITATION_INLINE_CODE_REGEX, "$1")
    .replace(CITATION_ORPHAN_PUNCT_REGEX, "$1$2")
    .replace(CITATION_ON_OWN_LINE_REGEX, " $1");
}

type MarkdownProps = {
  children: string;
  enableCellCitations?: boolean;
  citationWorkflowView?: "current-run" | "summary" | "dashboard";
};

function CellCitation({
  cellIndex,
  onClick,
}: {
  cellIndex: number;
  onClick: (cellIndex: number) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(cellIndex)}
      aria-label={`Jump to cell ${cellIndex}`}
      title={`Jump to cell ${cellIndex}`}
      className="mx-0.5 inline-flex h-4 min-w-4 translate-y-[-1px] items-center justify-center rounded-[4px] border border-white/55 bg-white px-1 text-black transition-colors duration-150 hover:border-white hover:bg-black hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
    >
      <span className="text-[9px] font-bold leading-none tabular-nums">
        {cellIndex}
      </span>
    </button>
  );
}

function splitTextWithCitations(
  text: string,
  onCellCitationClick: (cellIndex: number) => void,
  keyPrefix: string
): ReactNode {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  CELL_CITATION_REGEX.lastIndex = 0;

  while ((match = CELL_CITATION_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    parts.push(
      <CellCitation
        key={`${keyPrefix}-${match.index}-${match[1]}`}
        cellIndex={Number(match[1])}
        onClick={onCellCitationClick}
      />
    );

    lastIndex = CELL_CITATION_REGEX.lastIndex;
  }

  if (parts.length === 0) {
    return text;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

function injectCitationsIntoChildren(
  children: ReactNode,
  onCellCitationClick: (cellIndex: number) => void,
  keyPrefix: string
): ReactNode {
  return Children.map(children, (child, index) => {
    const childKey = `${keyPrefix}-${index}`;

    if (typeof child === "string") {
      return splitTextWithCitations(child, onCellCitationClick, childKey);
    }

    if (!isValidElement(child)) {
      return child;
    }

    const element = child as ReactElement<{ children?: ReactNode }>;
    const elementType = typeof element.type === "string" ? element.type : null;

    if (elementType === "code" || elementType === "pre") {
      return child;
    }

    if (element.props.children === undefined) {
      return child;
    }

    const transformedChildren = injectCitationsIntoChildren(
      element.props.children,
      onCellCitationClick,
      childKey
    );

    return cloneElement(element, { key: element.key ?? childKey }, transformedChildren);
  });
}

const remarkPlugins = [remarkGfm];

const NonMemoizedMarkdown = ({
  children,
  enableCellCitations = false,
  citationWorkflowView = "current-run",
}: MarkdownProps) => {
  const { setArtifact } = useArtifact();

  const handleCellCitationClick = useCallback(
    (cellIndex: number) => {
      setArtifact((prev) => ({
        ...prev,
        isVisible: true,
        workflowView: citationWorkflowView,
        pendingCellTarget: {
          cellIndex,
          requestedAt: Date.now(),
        },
      }));
    },
    [citationWorkflowView, setArtifact]
  );

  const renderWithCitations = useCallback(
    (content: ReactNode, keyPrefix: string) => {
      if (!enableCellCitations) {
        return content;
      }

      return injectCitationsIntoChildren(content, handleCellCitationClick, keyPrefix);
    },
    [enableCellCitations, handleCellCitationClick]
  );

  const components = useMemo<Partial<Components>>(
    () => ({
      // @ts-expect-error
      code: CodeBlock,
      pre: ({ children }) => <>{children}</>,
      ol: ({ node, children, ...props }) => {
        return (
          <ol className="list-decimal list-inside ml-4" {...props}>
            {renderWithCitations(children, "ol")}
          </ol>
        );
      },
      li: ({ node, children, ...props }) => {
        return (
          <li className="py-1 font-light" {...props}>
            {renderWithCitations(children, "li")}
          </li>
        );
      },
      ul: ({ node, children, ...props }) => {
        return (
          <ul
            className="list-decimal list-inside ml-4 mt-0 marker:text-gray-400"
            {...props}
          >
            {renderWithCitations(children, "ul")}
          </ul>
        );
      },
      strong: ({ node, children, ...props }) => {
        return (
          <span className="font-semibold" {...props}>
            {renderWithCitations(children, "strong")}
          </span>
        );
      },
      p: ({ node, children, ...props }) => {
        return (
          <p className="mb-0 leading-relaxed font-light" {...props}>
            {renderWithCitations(children, "p")}
          </p>
        );
      },
      a: ({ node, children, href, ...props }) => {
        const citationMatch =
          typeof href === "string"
            ? href.match(/^xynth:\/\/cell\/(\d+)$/i)
            : null;

        if (citationMatch) {
          return (
            <CellCitation
              cellIndex={Number(citationMatch[1])}
              onClick={handleCellCitationClick}
            />
          );
        }

        // Alert deep links get an icy-blue pill treatment so the user
        // can actually see them in dark chat bubbles. We accept BOTH
        // shapes:
        //   - absolute  https://xynth.com/alerts/{alert_id}    (legacy)
        //   - relative  /alerts/{alert_id}                     (preferred)
        // The relative form is what the curated replay session emits
        // (see `_patch_alert_url_relative.py`) so the link resolves
        // against the current origin — localhost in dev, xynth.com in
        // prod — instead of always punching out to the prod domain.
        // We require a non-empty path segment after /alerts/ so the
        // bare /alerts index page keeps the default style.
        const isAlertDeepLink =
          typeof href === "string" &&
          (
            /^https?:\/\/(?:www\.)?xynth\.com\/alerts\/[^/?#\s]+/i.test(href) ||
            /^\/alerts\/[^/?#\s]+/i.test(href)
          );

        if (isAlertDeepLink) {
          return (
            <Link
              href={href || "#"}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-baseline gap-1 font-semibold text-sky-300 hover:text-sky-200 underline decoration-sky-400/40 hover:decoration-sky-300/80 underline-offset-2 transition-colors"
              {...props}
            >
              {children}
            </Link>
          );
        }

        return (
          <Link
            href={href || "#"}
            className="text-gray-700 dark:text-gray-300 hover:underline"
            target="_blank"
            rel="noreferrer"
            {...props}
          >
            {children}
          </Link>
        );
      },
      h1: ({ node, children, ...props }) => {
        return (
          <h1 className="text-3xl font-semibold mt-6 mb-2" {...props}>
            {renderWithCitations(children, "h1")}
          </h1>
        );
      },
      h2: ({ node, children, ...props }) => {
        return (
          <h2 className="text-2xl font-semibold mt-4 mb-2" {...props}>
            {renderWithCitations(children, "h2")}
          </h2>
        );
      },
      h3: ({ node, children, ...props }) => {
        return (
          <h3 className="text-xl font-semibold mt-6 mb-2" {...props}>
            {renderWithCitations(children, "h3")}
          </h3>
        );
      },
      h4: ({ node, children, ...props }) => {
        return (
          <h4 className="text-lg font-semibold mt-6 mb-2" {...props}>
            {renderWithCitations(children, "h4")}
          </h4>
        );
      },
      h5: ({ node, children, ...props }) => {
        return (
          <h5 className="text-base font-semibold mt-6 mb-2" {...props}>
            {renderWithCitations(children, "h5")}
          </h5>
        );
      },
      h6: ({ node, children, ...props }) => {
        return (
          <h6 className="text-sm font-semibold mt-6 mb-2" {...props}>
            {renderWithCitations(children, "h6")}
          </h6>
        );
      },
      table: ({ node, children, ...props }) => {
        return (
          <div className="overflow-x-auto my-4 max-w-full">
            <table
              className="w-full border-collapse border border-border table-auto"
              {...props}
            >
              {renderWithCitations(children, "table")}
            </table>
          </div>
        );
      },
      thead: ({ node, children, ...props }) => {
        return (
          <thead className="bg-muted/50" {...props}>
            {renderWithCitations(children, "thead")}
          </thead>
        );
      },
      tbody: ({ node, children, ...props }) => {
        return <tbody {...props}>{renderWithCitations(children, "tbody")}</tbody>;
      },
      tr: ({ node, children, ...props }) => {
        return (
          <tr className="border-b border-border" {...props}>
            {renderWithCitations(children, "tr")}
          </tr>
        );
      },
      th: ({ node, children, ...props }) => {
        return (
          <th
            className="border border-border px-1.5 py-1 text-left font-semibold text-xs bg-muted/30"
            {...props}
          >
            {renderWithCitations(children, "th")}
          </th>
        );
      },
      td: ({ node, children, ...props }) => {
        return (
          <td
            className="border border-border px-1.5 py-1 text-xs break-words"
            {...props}
          >
            {renderWithCitations(children, "td")}
          </td>
        );
      },
      img: ({ node, src, alt, ...props }) => {
        return (
          <img
            src={src}
            alt={alt || ""}
            className="max-w-full h-auto rounded-lg my-4 border border-border"
            {...props}
          />
        );
      },
    }),
    [handleCellCitationClick, renderWithCitations]
  );

  const normalizedChildren = useMemo(
    () => (enableCellCitations ? normalizeCitations(children) : children),
    [children, enableCellCitations]
  );

  return (
    <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
      {normalizedChildren}
    </ReactMarkdown>
  );
};

export const Markdown = memo(
  NonMemoizedMarkdown,
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.enableCellCitations === nextProps.enableCellCitations &&
    prevProps.citationWorkflowView === nextProps.citationWorkflowView
);
